// [2026-09-02] ADDED — staff accounts. Superadmin only, enforced server-side.
//
// WHY THIS SCREEN EXISTS AT ALL, given the bootstrap login works: because `operator` +
// GATEWAY_OPERATOR_SECRET is a SHARED secret, and every action it takes is audited as
// "operator-bootstrap". Named accounts are what make the audit log answer "who", which is the only
// reason to keep an audit log. The bootstrap door stays for the 2am case; day-to-day work should
// name a person.
//
// A NEW ACCOUNT'S PASSWORD IS SHOWN ONCE AND NEVER STORED HERE, the same rule as an API secret.

import { useState } from 'react'
import {
  Empty, Modal, Notice, Skeleton, toast, when, useAsync,
} from '@incruiter/proctoring-client-frontend'
import { Badge } from '@incruiter/proctoring-client-frontend/src/components/ui/badge.jsx'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'
import { admin } from '../api.js'
// [2026-09-07] ADDED — the in-app dialog. See ask.jsx.
import { useAsk } from '../ask.jsx'

const ROLE_VARIANT = { superadmin: 'critical', admin: 'serious', viewer: 'idle' }

function CreateModal({ roles, onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'admin', password: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(event) {
    event.preventDefault()
    if (!form.email.trim() || !form.password) { setError('Email and password are required.'); return }
    if (form.password.length < 12) {
      // Mirrors the server's own rule so the person is told the constraint here rather than after
      // submitting. A staff credential reads every tenant's data; 12 is not arbitrary strictness.
      setError('Choose a password of at least 12 characters.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await admin.createUser({
        email: form.email.trim(), name: form.name.trim() || form.email.trim(),
        role: form.role, password: form.password,
      })
      toast('Staff account created')
      onCreated()
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  return (
    <Modal title="Create a staff account"
           subtitle="Named accounts are what make the audit log answer “who”." onClose={onClose}>
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={submit}>
        <label className="field"><span>Email</span>
          <input type="email" value={form.email} autoFocus onChange={set('email')} /></label>
        <label className="field"><span>Name</span>
          <input value={form.name} onChange={set('name')} placeholder="Full name" /></label>
        <label className="field"><span>Role</span>
          <select value={form.role} onChange={set('role')}
                  style={{ padding: '9px 10px', borderRadius: 'var(--radius-sm)', width: '100%',
                           border: '1px solid var(--border)', background: 'var(--surface)',
                           color: 'inherit', font: 'inherit' }}>
            {(roles || ['admin']).map((r) => <option key={r} value={r}>{r}</option>)}
          </select></label>
        <label className="field"><span>Initial password (min 12 characters)</span>
          <input type="password" value={form.password} onChange={set('password')}
                 autoComplete="new-password" /></label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</button>
        </div>
      </form>
    </Modal>
  )
}

export default function Staff({ onError, canManage }) {
  const [nonce, setNonce] = useState(0)
  const { data, error, loading } = useAsync(() => admin.users(), [nonce])
  const [creating, setCreating] = useState(false)
  // [2026-09-07] ADDED — ABOVE the early returns below, deliberately. Hooks must run
  // unconditionally and in the same order on every render; putting this after the `if (error)`
  // returns would skip it on the error and loading passes and break the hook order the moment the
  // data arrived. See ask.jsx.
  const [ask, askDialog] = useAsk()

  // A 403 here is not a failure to render — it is the correct answer for a non-superadmin. Saying so
  // beats an error banner that reads like the screen is broken.
  if (error && error.status === 403) {
    return <Notice kind="info">Only a superadmin can manage staff accounts.</Notice>
  }
  if (error) { onError?.(error); return <Notice kind="error">{error.message}</Notice> }
  if (loading || !data) return <Skeleton height={220} />

  const admins = data.admins || []

  async function setStatus(row, status) {
    // [2026-09-07] CHANGED — OLD: window.confirm(`… ${row.email}?`). See ask.jsx: this disables a
    // colleague's access, and a browser dialog is the wrong frame for it. The consequence is now
    // stated rather than left to the reader.
    if (!(await ask({
      title: `${status === 'disabled' ? 'Disable' : 'Enable'} ${row.email}?`,
      confirmLabel: status === 'disabled' ? 'Disable account' : 'Enable account',
      tone: status === 'disabled' ? 'danger' : 'primary',
      message: status === 'disabled'
        ? 'They are signed out on their next request — the role and status are re-read from the '
          + 'database on every call, so this takes effect immediately, not when their token expires.'
        : 'They can sign in again and their previous role is restored.',
    }))) return
    try {
      await admin.updateUser(row.admin_id, { status })
      toast('Account updated')
      setNonce((n) => n + 1)
    } catch (err) { toast(err.message, true) }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* [2026-09-07] ADDED — the in-app dialog. See ask.jsx. */}
      {askDialog}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                      flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Staff accounts</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {admins.length} account{admins.length === 1 ? '' : 's'}
            </div>
          </div>
          {canManage && <button className="btn" onClick={() => setCreating(true)}>+ Create account</button>}
        </div>

        {admins.length === 0 ? (
          <Empty title="No named staff accounts yet"
                 hint="You are signed in through the bootstrap operator door. Create a named account so the audit log records a person." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead><TableHead>Role</TableHead>
                <TableHead>Status</TableHead><TableHead>Created</TableHead>
                <TableHead style={{ textAlign: 'right' }}>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((a) => (
                <TableRow key={a.admin_id}>
                  <TableCell>
                    <div>{a.name || a.email}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>{a.email}</div>
                  </TableCell>
                  <TableCell><Badge variant={ROLE_VARIANT[a.role] || 'idle'}>{a.role}</Badge></TableCell>
                  <TableCell>
                    <Badge variant={a.status === 'active' ? 'healthy' : 'idle'}>{a.status}</Badge>
                  </TableCell>
                  <TableCell className="muted">{a.created_at ? when(a.created_at) : '—'}</TableCell>
                  <TableCell style={{ textAlign: 'right' }}>
                    {canManage && (
                      <button className="btn ghost"
                              onClick={() => setStatus(a, a.status === 'active' ? 'disabled' : 'active')}>
                        {a.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {creating && (
        <CreateModal roles={data.roles} onClose={() => setCreating(false)}
                     onCreated={() => { setCreating(false); setNonce((n) => n + 1) }} />
      )}
    </div>
  )
}
